import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Grid,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { IFlight, IGroundEvent, IPosition, IUserAppSettings } from "@vmosolutions/oc-interfaces";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { openSnackbar, updateUserAppSettings } from "src/redux/actions/App.Actions";
import { setCrewBase, setCrewGroupFilter } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { retrieveFlightData } from "src/redux/actions/flights/FlightData.Actions";
import {
  setAircraftFilters,
  setAircraftRowSettings,
  setAircraftSort,
  setUserSetScale,
} from "src/redux/actions/flights/FlightTimeline.Actions";
import { saveMosaicLayout } from "src/redux/actions/Mosaic.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAircraftFilterOption, isFlight } from "src/redux/reducers/FlightTimelineReducer";
import {
  CrewFilterAndSort,
  IPositionOptions,
  ISavedCrewScheduleDisplayOptions,
} from "../CrewSchedule/CrewScheduleHeader/CrewFilterAndSort";
import { TimelineFlightHighlight } from "../FlightTimeline/FlightTimelineHeader/TimelineFlightHighlight";
import { TimelineFocus } from "../FlightTimeline/FlightTimelineHeader/TimelineFocus";
import {
  aircraftFilterLabel,
  AircraftSortFilter,
  convertSelectedFiltersToFilterOptions,
} from "../FlightTimeline/FlightTimelineHeader/TimelineSortFilter";
import { TimelineViewScale } from "../FlightTimeline/FlightTimelineHeader/TimelineViewScale";
import { timeScales } from "../FlightTimeline/TimelineUtils";
import { BlockFormatPreference } from "./BlockFormatPreference";
import { CrewSchedTooltipToggle } from "./CrewSchedTooltipToggle";
import { MosaicLayout } from "./MosaicLayout";
import { StatusBarBehavior } from "./StatusBarBehavior";

interface IUserPreferencesWindowProps {
  id: string;
}

const UserPreferencesWindow = (props: IUserPreferencesWindowProps): JSX.Element => {
  const { id } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const [savedFilters, setSavedFilters] = useState<ISavedCrewScheduleDisplayOptions | null>(null);
  const userAppSettings: IUserAppSettings = useSelector((state: IRootState) => state.authentication.appSettings ?? {});
  const { flightTimelineViewScale, flightDisplayMenuCentered } = userAppSettings;
  const [crewPosition, setCrewPosition] = useState<IPositionOptions | null>(null);
  const positions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions).sort(
    (a, b) => (b.displayOrder > a.displayOrder ? -1 : 1)
  );
  const crewGroupFilter = useSelector((state: IRootState) => state.crewSchedule.groupFilter[id]);
  const filters = useSelector((state: IRootState) => state.timelineFlights.aircraftFilters);
  const aircraftGroups = useSelector((state: IRootState) => state.structures.customerStructure.aircraftGroups);
  const allAircraft = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);
  const selectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);
  const currentDate = useMemo(() => new Date(), []);
  const startDate = useMemo(
    () => new Date(new Date(currentDate).setDate(currentDate.getUTCDate() - 15)),
    [currentDate]
  );
  const endDate = useMemo(() => new Date(new Date(currentDate).setDate(currentDate.getUTCDate() + 15)), [currentDate]);

  const [viewScale, setViewScale] = useState<string>(flightTimelineViewScale ?? "");
  const [contentMenuCentered, setContentMenuCentered] = useState<boolean>(!!flightDisplayMenuCentered);

  const crewFilterRef = useRef<any>(null);
  const aircraftFilterRef = useRef<any>(null);
  const timelineViewRef = useRef<any>(null);
  const timelineHighlightRef = useRef<any>(null);
  const timelineFocusRef = useRef<any>(null);
  const blockFormatRef = useRef<any>(null);

  const [focusedMosaicButton, setFocusedMosaicButton] = useState("current");
  const [focusedStatusBarButton, setFocusedStatusBarButton] = useState(userAppSettings.statusBarBehavior);
  const [crewScheduleHideTooltips, setCrewScheduleHideTooltips] = useState(userAppSettings.crewScheduleHideTooltips);

  const aircraftFilters: IAircraftFilterOption[] = useMemo(
    () => convertSelectedFiltersToFilterOptions(filters, allAircraft, aircraftGroups),
    [filters, allAircraft, aircraftGroups]
  );
  const filteredAircraftLabel = useMemo(() => aircraftFilterLabel(aircraftFilters), [aircraftFilters]);
  //filter through positions to get get them in a format for selection menu
  const positionOptions: IPositionOptions[] = useMemo(() => {
    if (!positions || positions.length === 0) {
      return [];
    }
    const options = positions
      .map((position: IPosition) => {
        return {
          value: position.displayOrder,
          key: position.title,
          label: position.description,
          id: position._id,
        };
      })
      .sort((a, b) => a.value - b.value); //have them in display order

    //! Only shows all selection if crew group filter is applied.
    if (crewGroupFilter && crewGroupFilter.length > 0)
      options.unshift({ value: 0, key: "ALL", label: "ALL", id: undefined });

    return options;
  }, [crewGroupFilter, positions]);

  const selectCrewPosition = useCallback(
    (position: IPositionOptions): void => {
      if (!position) return;
      setCrewPosition(position);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userAppSettings]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSavedDisplayOptions = useCallback(
    debounce(async (windowId: string, dispatch: any, appSettings: IUserAppSettings | null): Promise<void> => {
      try {
        const savedFiltersObj = appSettings?.crewScheduleFilters;
        if (savedFiltersObj) {
          const { crewBase, groupFilter, position } = savedFiltersObj;
          if (crewBase) dispatch(setCrewBase(crewBase, windowId));
          if (groupFilter) dispatch(setCrewGroupFilter(groupFilter, windowId));
          if (position) setCrewPosition(position);
          setSavedFilters(savedFiltersObj);
        }
      } catch (err) {
        await AmplifyAPIWrapper.Put<any>("/me/settings", {
          crewScheduleFilters: null,
        });
      }
    }, 200),
    [userAppSettings]
  );

  const handleSave = useCallback(async (): Promise<void> => {
    try {
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "info",
          message: "Saving User Preferences...",
        })
      );
      const saveItems = {
        statusBarBehavior: focusedStatusBarButton,
        crewScheduleHideTooltips: crewScheduleHideTooltips,
      };

      if (crewFilterRef?.current?.hasOwnProperty("saveDefault")) {
        Object.assign(saveItems, { crewScheduleFilters: crewFilterRef.current.saveDefault() });
      }
      if (aircraftFilterRef?.current?.hasOwnProperty("saveDefault")) {
        Object.assign(saveItems, { timelineAircraftFilters: aircraftFilterRef.current.saveDefault() });
      }
      if (timelineViewRef?.current?.hasOwnProperty("saveDefault")) {
        Object.assign(saveItems, {
          flightTimelineRowHeightPercentage: timelineViewRef.current.saveDefault().flightTimelineRowHeightPercentage,
        });
        Object.assign(saveItems, {
          flightTimelineViewScale: timelineViewRef.current.saveDefault().flightTimelineViewScale,
        });
      }
      if (timelineHighlightRef?.current?.hasOwnProperty("SaveDefault")) {
        Object.assign(saveItems, { highlightFlightSettings: timelineHighlightRef.current.SaveDefault() });
      }
      if (timelineFocusRef?.current?.hasOwnProperty("saveDefault")) {
        Object.assign(saveItems, { flightTimeline: timelineFocusRef.current.saveDefault() });
      }
      if (blockFormatRef?.current?.hasOwnProperty("saveDefault")) {
        Object.assign(saveItems, { blockInfo: blockFormatRef.current.saveDefault() });
      }

      if (focusedMosaicButton == "reset") {
        dispatch(saveMosaicLayout(true));
        setFocusedMosaicButton("current");
      }

      const sbResponse = await AmplifyAPIWrapper.Put<any>("/me/settings", saveItems);
      if (sbResponse) {
        dispatch(updateUserAppSettings(sbResponse));
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "success",
            message: "User Preferences Saved",
          })
        );
      }
    } catch (e) {
      console.error(e);
      dispatch(
        openSnackbar({
          open: true,
          closeDelay: 3000,
          variant: "error",
          message: "Unable to Save Preferences",
        })
      );
    }
  }, [dispatch, focusedMosaicButton, focusedStatusBarButton, crewScheduleHideTooltips, contentMenuCentered]);

  const handleRestoreDefault = useCallback(async (): Promise<void> => {
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "info",
        message: "Restoring Defaults...",
      })
    );
    const response = await AmplifyAPIWrapper.Put<IUserAppSettings>("/me/settings", {});
    if (response?.timelineAircraftFilters) {
      dispatch(
        setAircraftSort(
          response?.timelineAircraftFilters.aircraftSort ?? {
            value: "default_sorting",
            key: [],
            dir: [],
            label: "Default",
          }
        )
      );
      dispatch(setAircraftFilters(response?.timelineAircraftFilters.aircraftFilter ?? []));
    }
    if (response?.flightTimelineViewScale) {
      setViewportScale(response?.flightTimelineViewScale);
    }
    if (response?.flightTimelineRowHeightPercentage) {
      dispatch(setAircraftRowSettings(response.flightTimelineRowHeightPercentage));
    }
    if (response?.highlightFlightSettings) {
      const updatedAppSettings = {
        ...response,
        highlightFlightSettings: { ...response?.highlightFlightSettings, lastApplied: new Date()?.valueOf() },
      };
      dispatch(updateUserAppSettings(updatedAppSettings));
    }
    if (response?.blockInfo) {
      const updatedAppSettings = {
        ...response,
        blockInfo: { ...userAppSettings.blockInfo, ...{ utc: response.blockInfo.utc, icao: response.blockInfo.icao } },
      };
      dispatch(updateUserAppSettings(updatedAppSettings));
    }
    timelineFocusRef.current.refresh();
    setFocusedMosaicButton("current");
    setCrewScheduleHideTooltips(false);
    dispatch(
      openSnackbar({
        open: true,
        closeDelay: 3000,
        variant: "success",
        message: "Default Values Restored",
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (id && dispatch) getSavedDisplayOptions(id, dispatch, userAppSettings);
  }, [id, dispatch, getSavedDisplayOptions, userAppSettings]);

  const dataLoadedCheck = useCallback(
    (minTime: number, maxTime: number): void => {
      dispatch(retrieveFlightData(new Date(minTime), new Date(maxTime)));
    },
    [dispatch]
  );

  const setViewportScale = useCallback(
    (scale?: string | number[]) => {
      if (!!scale && typeof scale === "string" && timeScales.hasOwnProperty(scale)) {
        setViewScale(scale);
      } else if (!!scale && Array.isArray(scale) && scale.length === 2) {
        const [minTime, maxTime] = scale;
        dataLoadedCheck(minTime, maxTime);
      }
    },
    [dataLoadedCheck]
  );

  const setFocusOnEvent = useCallback(
    (eventObj: IFlight | IGroundEvent): void => {
      const event: IFlight | IGroundEvent | any = eventObj;
      const startTime = isFlight(event) ? new Date(event.actualOff).getTime() : new Date(event.actualStart).getTime();
      const endTime = isFlight(event) ? new Date(event.actualOn).getTime() : new Date(event.actualEnd).getTime();

      const scaleStartTime = startTime - timeScales.hour;
      const scaleEndTime = endTime + timeScales.hour;

      // Save user Scale
      dispatch(setUserSetScale([scaleStartTime, scaleEndTime]));
    },
    [dispatch]
  );

  const setFocusOnDate = useCallback(
    (jumpDate: Date): void => {
      const jumpTime =
        typeof jumpDate === "object" && typeof jumpDate.getMonth === "function"
          ? jumpDate.getTime()
          : new Date().getTime();
      setViewportScale("day");

      const scaleStartTime = jumpTime - timeScales.day / 2;
      const scaleEndTime = jumpTime + timeScales.day / 2;
      dispatch(setUserSetScale([scaleStartTime, scaleEndTime]));
      // LOAD data if not already loaded.
      if (dataLoadedCheck && typeof dataLoadedCheck === "function") {
        dataLoadedCheck(scaleStartTime, scaleEndTime);
      }
    },
    [dataLoadedCheck, dispatch, setViewportScale]
  );

  return (
    <div>
      <Card square={true}>
        <CardContent>
          <Grid container direction={"row"} spacing={1} sx={{ paddingBottom: "10px" }}>
            <Grid item>
              <Button
                className="btn reset"
                onClick={handleRestoreDefault}
                variant="contained"
                color="secondary"
                size="small"
              >
                Refresh
              </Button>
            </Grid>
            <Grid item>
              <Button className={"btn save"} onClick={handleSave} variant="contained" color="primary" size="small">
                Save
              </Button>
            </Grid>
          </Grid>
          <Accordion defaultExpanded={true} sx={{ backgroundColor: theme.palette.background.default }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Crew Schedule</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2">Crew Schedule Filters</Typography>
              <CrewFilterAndSort
                windowId={id}
                positionHandler={selectCrewPosition}
                positionOptions={positionOptions}
                currentPosition={crewPosition?.label ?? ""}
                savedFilters={savedFilters ?? undefined}
                setSavedFilters={setSavedFilters}
                groupSave={true}
                ref={crewFilterRef}
              />
              <Typography variant="subtitle2">Hoverable flight information on schedule</Typography>
              <CrewSchedTooltipToggle
                crewScheduleHideTooltips={crewScheduleHideTooltips}
                setCrewScheduleHideTooltips={setCrewScheduleHideTooltips}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded={true} sx={{ backgroundColor: theme.palette.background.default }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Flight Display</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ marginTop: 3 }} variant="subtitle2">
                Flight Display Aircraft Filters
              </Typography>
              <AircraftSortFilter
                groupSave={true}
                aircraftFilters={aircraftFilters}
                filteredAircraftLabel={filteredAircraftLabel}
                ref={aircraftFilterRef}
              />
              <Typography sx={{ marginTop: 3 }} variant="subtitle2">
                Flight Display View
              </Typography>
              <TimelineViewScale
                setViewportScale={setViewportScale}
                viewScale={viewScale}
                groupSave={true}
                ref={timelineViewRef}
              />
              <Typography sx={{ marginTop: 3 }} variant="subtitle2">
                Flight Highlight
              </Typography>
              <TimelineFlightHighlight groupSave={true} ref={timelineHighlightRef} />
              <Typography sx={{ marginTop: 3 }} variant="subtitle2">
                Flight Display Block Formatting
              </Typography>
              <BlockFormatPreference id={id} ref={blockFormatRef} />
              <Typography sx={{ marginTop: 3 }} variant="subtitle2">
                Flight Display Timeline Focus
              </Typography>
              <TimelineFocus
                selectedEvents={selectedEvents}
                setFocusOnEvent={setFocusOnEvent}
                setFocusOnDate={setFocusOnDate}
                setViewportScale={setViewportScale}
                startDate={startDate}
                endDate={endDate}
                groupSave={true}
                ref={timelineFocusRef}
              />
              <Typography variant="subtitle2">Vertically Center Flight Context Menu</Typography>
              <SwitchField
                className={`toggle_label ${!userAppSettings.flightDisplayMenuCentered && "active"}`}
                name="UTC_Local"
                value={contentMenuCentered}
                onChange={(e) => {
                  setContentMenuCentered(e);
                }}
                switchlabels={["Yes", "No"]}
                switchtooltips={["Yes", "No"]}
                label=""
                allowNull={false}
              />
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded={true} sx={{ backgroundColor: theme.palette.background.default }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">General</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ marginTop: 3 }} variant="subtitle2">
                Mosaic Layout
              </Typography>
              <MosaicLayout focusedButton={focusedMosaicButton} setFocusedButton={setFocusedMosaicButton} />
              <Typography sx={{ marginTop: 3 }} variant="subtitle2">
                Status Bar Behavior
              </Typography>
              <StatusBarBehavior focusedButton={focusedStatusBarButton} setFocusedButton={setFocusedStatusBarButton} />
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};

export { UserPreferencesWindow };
